import { z } from 'zod';
import { HandleSchema } from './common';

/** RFC 7033 WebFinger query: ?resource=acct:handle@host */
export const WebfingerQuerySchema = z.object({
  resource: z.string().min(6).max(320),
});

export const JRDLinkSchema = z.object({
  rel: z.string(),
  type: z.string().optional(),
  href: z.string().optional(),
});

/** JSON Resource Descriptor (RFC 7033 §4.4). */
export const JRDSchema = z.object({
  subject: z.string(),
  aliases: z.array(z.string()).optional(),
  links: z.array(JRDLinkSchema),
});

export const InstanceMetadataSchema = z.object({
  name: z.string(),
  description: z.string(),
  software: z.object({ name: z.string(), version: z.string() }),
  api: z.string(),
  openapi: z.string(),
  mcp: z.string(),
  llmsTxt: z.string(),
  export: z.object({
    policy: z.string(),
    endpoint: z.string(),
  }),
  federation: z.object({
    activitypub: z.boolean(),
    inboundFollows: z.boolean().describe('Always false today — not implemented. See docs/open-platform.md'),
    webfinger: z.string(),
    actorTemplate: z.string(),
  }),
});

export const NodeInfoDiscoverySchema = z.object({
  links: z.array(z.object({ rel: z.string(), href: z.string() })),
});

/** NodeInfo 2.1 (http://nodeinfo.diaspora.software/ns/schema/2.1). Only real aggregate counts. */
export const NodeInfoSchema = z.object({
  version: z.literal('2.1'),
  software: z.object({ name: z.string(), version: z.string(), repository: z.string().optional() }),
  protocols: z.array(z.string()),
  services: z.object({ inbound: z.array(z.string()), outbound: z.array(z.string()) }),
  openRegistrations: z.boolean(),
  usage: z.object({
    users: z.object({
      total: z.number().int().nonnegative(),
      activeMonth: z.number().int().nonnegative(),
      activeHalfyear: z.number().int().nonnegative(),
    }),
    localPosts: z.number().int().nonnegative(),
  }),
  metadata: z.record(z.string(), z.unknown()),
});

/** Minimal ActivityStreams 2.0 Person actor. */
export const ActorSchema = z.object({
  '@context': z.array(z.string()),
  id: z.string(),
  type: z.literal('Person'),
  preferredUsername: HandleSchema,
  name: z.string(),
  summary: z.string().optional(),
  inbox: z.string(),
  outbox: z.string(),
  url: z.string(),
  publicKey: z.object({
    id: z.string(),
    owner: z.string(),
    publicKeyPem: z.string(),
  }),
});

export const ApNoteSchema = z.object({
  id: z.string(),
  type: z.literal('Note'),
  attributedTo: z.string(),
  content: z.string(),
  url: z.string(),
  published: z.string(),
  to: z.array(z.string()),
});

export const CreateActivitySchema = z.object({
  '@context': z.array(z.string()),
  id: z.string(),
  type: z.literal('Create'),
  actor: z.string(),
  published: z.string(),
  to: z.array(z.string()),
  object: ApNoteSchema,
});

export const OutboxPageSchema = z.object({
  '@context': z.array(z.string()),
  id: z.string(),
  type: z.literal('OrderedCollectionPage'),
  partOf: z.string(),
  orderedItems: z.array(CreateActivitySchema),
  next: z.string().nullable(),
});

export const ActorHandleParamsSchema = z.object({ handle: z.string().min(1).max(60) });
export const OutboxQuerySchema = z.object({
  cursor: z.string().min(1).max(512).optional(),
});
